<template>
  <b-card>
    <b-row class="">
      <b-col class="col-md-12">
        <h2
            class="text-blue font-weight-bold col-md-10 col-lg-10 col-xl-11 mt-1"
        >
          {{ $t(title) }}
        </h2>
      </b-col>
    </b-row>

    <b-row class="my-3">
      <b-col class="col-md-2">
        <b-form-group
            id="start_date"
            :label="$t('start_date') + ':'"
            label-for="start-date-input"
        >
          <b-form-input
              id="start-date-input"
              v-model.trim="searchData.start_date"
              type="date"
          />
        </b-form-group>
      </b-col>
      <b-col class="col-md-2">
        <b-form-group
            id="end_date"
            :label="$t('end_date') + ':'"
            label-for="end-date-input"
        >
          <b-form-input
              id="end-date-input"
              v-model.trim="searchData.end_date"
              type="date"
          />
        </b-form-group>
      </b-col>
      <b-col class="col-md-2">
        <b-form-group
            id="seller_debtor"
            :label="$t('seller_debtor') + ':'"
            label-for="seller-debtor-input"
        >
          <b-form-input
              id="seller-debtor-input"
              v-model.trim="searchData.seller_debtor"
          />
        </b-form-group>
      </b-col>
      <b-col class="col-md-2">
        <b-form-group
            id="status"
            :label="$t('status') + ':'"
            label-for="status-select"
        >
          <b-form-select
              id="status-select"
              v-model.trim="searchData.invoice_status"
              :options="statusOptions"
          />
        </b-form-group>
      </b-col>
      <b-col class="col-md-2">

        <b-form-group
            id="hide-market"
            :label="$t('hide_from_market') + ':'"
            label-for="hide-market-select"
        >
          <b-form-select
              id="hide-market-select"
              v-model.trim="searchData.hide_from_market"
              :options="hide_market_options"
          />
        </b-form-group>
      </b-col>
      <b-col class="col-md-1">
        <b-button
            variant="secondary"
            class="mt-2"
            @click="getApiData"
        >
          {{ $t('search') }}
        </b-button>
      </b-col>
      <b-col class="col-md-1">
        <b-button
            v-if="!exportLoading"
            variant="secondary"
            class="mt-2"
            @click="exportData()"
        >
          Export
        </b-button>
      </b-col>
    </b-row>

    <Table
        :key="totalItems"
        large
        :fields="fields"
        :url="url"
        :show-search="false"
        :body="searchData"
        :update-data="updateData"
        responsive="sm"
    >
      <template #cell(id)="data">
        <b-link
            :to="adminType(['super_admin','admin_type_a']) ? '/invoice/' + data.item.id : null"
            class="badge badge-light-primary"
            :tabindex="adminType(['super_admin','admin_type_a']) ? 0 : -1"
            :style="adminType(['super_admin','admin_type_a']) ? 'width:55px;' : 'width:55px; pointer-events: none;'"
        >
          I-{{ data.item.id }}
        </b-link>
      </template>

      <template #cell(settlement_date)="data">
        <span v-if="data.item.settlement_date">{{ data.item.settlement_date }}</span>
        <span v-else>-</span>
      </template>

      <template #cell(default_date)="data">
        <span v-if="data.item.default_date">{{ data.item.default_date }}</span>
        <span v-else>-</span>
      </template>

      <template #cell(seller)="data">
        <b-link
            v-if="data.item.company"
            :to="'/company/' + data.item.company.id"
        >
          {{ data.item.company.name }}
        </b-link>
      </template>
      <template #cell(seller_id)="data">
        <span v-if="data.item.company">
          {{ data.item.company.id }}
        </span>
        <span v-else>-</span>
      </template>
      <template #cell(amount)="data">
        <span v-if="data.item.amount">
          {{ amountFormat(data.item.amount, data.item.currency.name) }}
        </span>
        <span v-else>-</span>
      </template>
      <template #cell(investment_amount)="data">
        <span v-if="data.item.scoring">
          {{amountFormat(CalculationsHelper.getInvestmentAmount(data.item), data.item.currency.name)}}
        </span>
        <span v-else>-</span>
      </template>
      <template #cell(liquidity_amount)="data">
        <span v-if="data.item.scoring">
          {{amountFormat(CalculationsHelper.getLiquidityAmount(data.item), data.item.currency.name)}}
        </span>
        <span v-else>-</span>
      </template>
      <template #cell(refund_amount)="data">
        <span v-if="data.item.scoring">
          {{amountFormat(CalculationsHelper.getRefundAmount(data.item), data.item.currency.name) }}
        </span>
        <span v-else>-</span>
      </template>

      <template #cell(refund_percent)="data">
        <span v-if="data.item.scoring">
          {{CalculationsHelper.getRefundPercentage(data.item) }}
        </span>
        <span v-else>-</span>
      </template>

      <template #cell(refund_default_amount)="data">
        <span v-if="data.item.scoring">
          {{amountFormat(CalculationsHelper.getRefundDefaultAmount(data.item), data.item.currency.name) }}
        </span>
        <span v-else>-</span>
      </template>

      <template #cell(refund_default_percent)="data">
        <span v-if="data.item.scoring">
          {{CalculationsHelper.getRefundDefaultPercentage(data.item) }}
        </span>
        <span v-else>-</span>
      </template>

      <template #cell(aequitex_fee)="data">
        <span v-if="data.item.scoring">{{ amountFormat(CalculationsHelper.getAequitexFeeSellerAmount(data.item), data.item.currency.name) }}</span>
        <span v-else>-</span>
      </template>

      <template #cell(aequitex_fee_percent)="data">
        <span v-if="data.item.scoring">{{ CalculationsHelper.getAequitexFeeSellerPercentage(data.item) }}</span>
        <span v-else>-</span>
      </template>

      <template #cell(aequitex_fee_2)="data">
        <span v-if="data.item.scoring">{{ amountFormat(CalculationsHelper.getAequitexFeeInvestorAmount(data.item), data.item.currency.name) }}</span>
        <span v-else>-</span>
      </template>

      <template #cell(aequitex_fee_2_percent)="data">
        <span v-if="data.item.scoring">{{ CalculationsHelper.getAequitexFeeInvestorPercentage(data.item) }}</span>
        <span v-else>-</span>
      </template>

      <template #cell(roi)="data">
        <span v-if="data.item.scoring">{{ amountFormat(CalculationsHelper.getRoiAmount(data.item), data.item.currency.name) }}</span>
        <span v-else>-</span>
      </template>

      <template #cell(roi_percent)="data">
        <span v-if="data.item.scoring">~{{ CalculationsHelper.getRoiPercentage(data.item) }}</span>
        <span v-else>-</span>
      </template>

      <template #cell(shown_investor_roi_amount)="data">
        <span v-if="data.item.scoring">~{{ CalculationsHelper.getInvestorRoiAmount(data.item) }}</span>
        <span v-else>-</span>
      </template>

      <template #cell(shown_investor_roi_percent)="data">
        <span v-if="data.item.scoring">~{{ CalculationsHelper.getInvestorRoiPercentage(data.item) }}</span>
        <span v-else>-</span>
      </template>

      <template #cell(debtor)="data">
        <b-link
            v-if="data.item.debtor"
            :to="'/company/' + data.item.debtor.id"
        >{{ data.item.debtor.name }}
        </b-link>
        <div v-else>
          -
        </div>
      </template>

      <template #cell(debtor_id)="data">
        <span v-if="data.item.debtor">{{ data.item.debtor.id }}</span>
        <span v-else>-</span>
      </template>

      <template #cell(investor)="data">
        <p v-if="data.item.investor">
          {{ data.item.investor }}
        </p>
        <div v-else>
          -
        </div>
      </template>

      <template #cell(currency)="data">
        <span>{{ data.item.currency.name }} </span>
      </template>
      <template #cell(invoice_number)="data">
        <span
            v-if="data.item.invoice_number"
        >{{ data.item.invoice_number }}
        </span>
        <span v-else> - </span>
      </template>
      <template #cell(created_at)="data">
        {{ data.item.created_at | formatDate }}
      </template>
      <template #cell(hide_from_market)="data">
        <span v-if="data.value == 1" class="text-danger">
          {{ $t("hidden") }}</span>
        <span v-else-if="data.value == 0" class="text-success">
          {{ $t("shown") }}</span>
      </template>
      <template #cell(status)="data">
        <span :class="getStatus(data).class">
          {{ $t(getStatus(data).label) }}
        </span>
      </template>

      <template #cell(action)="data">
        <b-button
            v-b-tooltip.hover
            :title="$t('read_pdf')"
            class="mr-1 button-top-m web"
            variant="outline-success"
            size="sm"
            @click="readPdf(data.item.id, data.item)"
        >
          <FeatherIcon icon="BookOpenIcon"/>
        </b-button>
      </template>
      <template #cell(silent)="data">
        <span v-if="data.item.recourse == 0">-</span>
        <span v-if="data.item.recourse == 1">Yes</span>
      </template>
      <template #cell(recourse)="data">
        <span v-if="data.value == 0">-</span>
        <span v-if="data.value == 1">Yes</span>
      </template>
      <template #cell(custom)="">
      </template>
    </Table>
    <ApproveModal
        :text-content="modalText"
        :title="modalTitle"
    />
  </b-card>
</template>

<script>
import {
  BTable,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BPagination,
  BTr,
  BTh,
  BButton,
  BLink,
} from 'bootstrap-vue'
import fileDownload from 'js-file-download'
import ApproveModal from '@/components/Common/ApproveModal'
import InvoiceUpdate from '@/components/Invoice/InvoiceUpdate'
import CustomLoader from '@/components/Common/CustomLoader'
import Table from '@/components/Common/Table2'
import Ripple from 'vue-ripple-directive'
import {CurrencyAmountFormat} from '@core/utils/CurrencyAmountFormat'
import CalculationsHelper from "@/helpers/calculationsHelper";

export default {
  name: 'Invoices',
  components: {
    ApproveModal,
    Table,
    BTable,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BPagination,
    BTr,
    BTh,
    BButton,
    BLink,
    CustomLoader,
    InvoiceUpdate,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      updateData: 0,
      CalculationsHelper,
      title: 'invoices_management',
      companies: [],
      modalText: null,
      modalTitle: null,
      options: null,
      fields1: [
        {
          label: 'title', align: 'start', key: 'title', sortable: false,
        },
        {
          label: '', customSlot: false, key: 'custom', class: 'text-right',
        },
      ],
      fields: [
        {
          label: 'created_at', align: 'start', key: 'created_at', sortable: true,
        },
        {
          text: 'Reference', align: 'start', key: 'id', sortable: true,
        },
        {
          label: "issue_date",
          align: "start",
          key: "issue_date",
          sortable: false,
        },
        {
          label: 'publication_date',
          align: 'start',
          key: 'enable_date',
          sortable: true,
        },
        {
          text: 'Sold Date',
          align: 'start',
          key: 'sold_date',
          sortable: true,
        },
        {
          text: 'Due date',
          align: 'start',
          key: 'due_date',
          sortable: true,
        },
        {
          text: 'Settlement Date',
          align: 'start',
          key: 'settlement_date',
          sortable: true,
        },
        {
          text: 'Default Date',
          align: 'start',
          key: 'default_date',
          sortable: true,
        },
        {
          text: 'Seller ID', align: 'start', key: 'seller_id', sortable: true,
        },
        {
          text: 'Seller', align: 'start', key: 'seller', sortable: true,
        },
        {
          text: 'Debtor ID', align: 'start', key: 'debtor_id', sortable: true,
        },
        {
          text: 'Debtor', align: 'start', key: 'debtor', sortable: true,
        },
        {
          text: 'Investor', align: 'start', key: 'investor', sortable: true,
        },
        {
          text: 'Invoice Nr',
          align: 'start',
          key: 'invoice_number',
          sortable: true,
        },
        {
          text: 'Currency', align: 'start', key: 'currency', sortable: true,
        },
        {
          text: 'Invoice Amount',
          align: 'start',
          key: 'amount',
          sortable: true,
        },
        {
          text: 'Investment Amount',
          align: 'start',
          key: 'investment_amount',
          sortable: true,
        },
        {
          text: 'Liquidity Amount',
          align: 'start',
          key: 'liquidity_amount',
          sortable: true,
        },
        {
          text: 'Refund',
          align: 'start',
          key: 'refund_amount',
          sortable: true,
        },

        {
          text: 'Refund percent',
          align: 'start',
          key: 'refund_percent',
          sortable: true,
        },

        {
          text: 'Refund (Default)',
          align: 'start',
          key: 'refund_default_amount',
          sortable: true,
        },

        {
          text: 'Refund percent (Default)',
          align: 'start',
          key: 'refund_default_percent',
          sortable: true,
        },

        {
          text: 'Aequitex Fee',
          label: 'Aequitex Fee 1 (Seller)',
          align: 'start',
          key: 'aequitex_fee',
          sortable: true,
        },
        {
          text: 'Aequitex Fee percent',
          label: 'Aequitex Fee 1 (Seller) Percent',
          align: 'start',
          key: 'aequitex_fee_percent',
          sortable: true,
        },
        {
          text: 'Aequitex Fee 2 (Investor)',
          label: 'Aequitex Fee 2 (Investor)',
          align: 'start',
          key: 'aequitex_fee_2',
          sortable: false,
        },
        {
          text: 'Aequitex Fee 2 (Investor) Percent',
          label: 'Aequitex Fee 2 (Investor) Percent',
          align: 'start',
          key: 'aequitex_fee_2_percent',
          sortable: true,
        },
        {
          text: 'Roi',
          align: 'start',
          key: 'roi',
          sortable: true,
        },
        {
          text: 'Roi Percent',
          align: 'start',
          key: 'roi_percent',
          sortable: true,
        },
        {
          text: 'Shown Investor Roi Amount',
          align: 'start',
          key: 'shown_investor_roi_amount',
          sortable: true,
        },
        {
          text: 'Shown Investor Roi Percent',
          align: 'start',
          key: 'shown_investor_roi_percent',
          sortable: true,
        },

        {
          text: 'Silent', align: 'start', key: 'silent', sortable: true,
        },
        {
          text: 'Recourse', align: 'start', key: 'recourse', sortable: true,
        },
        {
          text: 'Market Visibility', key: 'hide_from_market', customSlot: true, sortable: true,
        },
        {
          text: 'Status', key: 'status', customSlot: true, sortable: true,
        },
        {
          text: '', customSlot: true, key: 'action', class: '',
        },
      ],
      items: [],
      page: 1,
      pageCount: 0,
      itemsPerPage: 12,
      status: [],
      isLoading: false,
      totalItems: 0,
      updateInvoiceId: null,
      url: '/invoice/admin/list',
      approveModalAction: null,
      item: null,
      showPdfViwer: false,
      loadPdf: true,
      type: '',
      base64: '',
      source: null,
      embedType: '',
      src: '',
      hide_market_options: [
        {value: null, text: this.$t('choose')},
        {value: 0, text: this.$t('shown')},
        {value: 1, text: this.$t('hidden')},
      ],
      searchData: {},
    }
  },
  computed: {
    rows() {
      return this.totalItems
    },
    statusOptions(){
      return [
          {value: null, text: this.$t('select_one')},
          {value: 0, text: this.$t('Open')},
          {value: 1, text: this.$t('active_marketplace_offer')},
          {value: 2, text: this.$t('rejected')},
          {value: 6, text: this.$t('debtor_confirmation_required')},
          {value: 8, text: this.$t('soon_goes_active')},
          {value: 10, text: this.$t('sold')},
          {value: 11, text: this.$t('settled')},
          {value: 12, text: this.$t('send_offer')},
          {value: 13, text: this.$t('invoice_default')},
          {value: 16, text: this.$t("required_seller_confirmation")},
          ]
    }
  },
  created() {
    this.getCompanies()
    this.getGlobalSetting()
  },
  methods: {
    getStatus({ value, item }) {
      const gs = this.global_settings;

      const map = {
        0: { class: "text-secondary", label: "Open" },
        1: { class: "text-success", label: "active_marketplace_offer" },
        2: (item, gs) => gs?.status && !item?.msg ? { class: "text-secondary", label: "send_offer" } : { class: "text-danger", label: "rejected" },
        3: { class: "text-secondary", label: "scoring_required" },
        4: { class: "text-secondary", label: "scored" },
        5: { class: "text-secondary", label: "click_sell_invoice" },
        6: (item) =>
            item?.recourse === 0
                ? {
                  class: "text-secondary",
                  label:
                      item?.is_reverse === 1
                          ? "supplier_confirmation_required"
                          : "debtor_confirmation_required",
                }
                : { class: "text-secondary", label: "-" },
        7: { class: "text-secondary", label: "rejected" },
        8: { class: "text-secondary", label: "soon_goes_active" },
        9: { class: "text-secondary", label: "in_review" },
        10: { class: "text-secondary", label: "sold" },
        11: { class: "text-secondary", label: "settled" },
        12: { class: "text-secondary", label: "send_offer" },
        13: { class: "text-secondary", label: "invoice_default" },
        14: { class: "text-danger", label: "deactivated" },
        15: { class: "text-danger", label: "removed_from_marketplace" },
        16: { class: "text-secondary", label: "required_seller_confirmation" },
      };

      const entry = map[value];

      if (!entry) return { class: "text-muted", label: "unknown" };

      return typeof entry === "function" ? entry(item, gs) : entry;
    },

    async exportData() {
      this.exportLoading = true

      try {
        const response = await this.$http.post('/invoice/admin/export',
            {
              start_date: this.searchData.start_date,
              end_date: this.searchData.end_date,
              seller_debtor: this.searchData.seller_debtor,
              invoice_status: this.searchData.invoice_status,
              hide_from_market: this.searchData.hide_from_market,
            },
            {responseType: 'blob'})

        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'Invoices List.xlsx')
        document.body.appendChild(link)
        link.click()
        this.exportLoading = false
      } catch (error) {
        console.error('Error exporting data:', error)
        this.exportLoading = false
      }
    },

    amountFormat(amount, currency) {
      return CurrencyAmountFormat(amount, currency)
    },

    async getGlobalSetting() {
      await this.$http.post('/settings/get/get_offer_later').then(res => {
        if (res) {
          if (res.data.value) {
            this.global_settings = parseInt(res.data.value)
          }
        }
      })
    },

    readPdf(id, item) {
      this.item = item
      this.$store.commit('app/ON_SIDEBAR_CLICK', false)
      this.loadPdf = true
      this.type = ''
      this.base64 = ''
      this.src = ''
      this.embedType = ''
      this.$http
          .post(
              `/invoice/admin/download/${id}`,
              {},
              {
                responseType: 'blob',
              },
          )
          .then(response => {
            fileDownload(response.data, item.title)
          })
          .catch(() => {
          })
    },
    getCompanies() {
      this.$http.post('/company/admin/allList', {}).then(response => {
        if (response.data.status === 200) {
          this.companies = response.data.value
        }
      })
    },
    changeModalInfo(action, title, text, id) {
      this.approveModalAction = action
      this.modalText = text
      this.modalTitle = title
      this.idToChange = id
    },
    callApproveModalAction() {
      if (this.approveModalAction === 'approve') {
        this.changeStatus(this.idToChange, 'approve')
      }
      if (this.approveModalAction === 'decline') {
        this.changeStatus(this.idToChange, 'decline')
      }
      if (this.approveModalAction === 'delete') {
        this.changeStatus(this.idToChange, 'delete')
      }
    },
    changeStatus(id, status) {
      this.isLoading = true
      let s = 0
      if (status === 'approve') {
        s = 1
      }
      if (status === 'decline') {
        s = 2
      }
      if (status === 'delete') {
        s = 99
      }
      this.$http
          .post(`/invoice/admin/update/${id}`, {status: s})
          .then(() => {
            this.getApiData()
            this.isLoading = false
          })
          .catch(() => {
            this.isLoading = false
          })
    },
    downloadPdf(id, item) {
      this.$http
          .post(
              `/invoice/admin/download/${id}`,
              {},
              {
                responseType: 'blob',
              },
          )
          .then(response => {
            fileDownload(response.data, item.title)
            this.isLoading = false
          })
          .catch(() => {
            this.isLoading = false
          })
    },
    getApiData() {
      this.isLoading = true
      this.items = []
      this.updateData++;
    },
  },
}
</script>

<style scoped>
</style>
